import { api } from '../axios-instance'
import axios, { AxiosError } from 'axios'
import { API_TIMETABLE_URL } from '@/config/constants'

// External timetable API URLs
const EXTERNAL_TIMETABLE_API =
  `${API_TIMETABLE_URL}/generate-schedule`
const EXTERNAL_TIMETABLE_JOBS_API = `${API_TIMETABLE_URL}/timetable-jobs`
const EXTERNAL_COLOR_SCHEDULE_API =
  `${API_TIMETABLE_URL}/analyze-schedule`
const EXTERNAL_VALIDATE_HARD_API =
  `${API_TIMETABLE_URL}/analyze_hard`
const EXTERNAL_ANALYZE_SOFT_API =
  `${API_TIMETABLE_URL}/analyze_soft`
const EXTERNAL_TIME_SLOTS_API = '/timeslots'

// Save timetable API URL is handled by the axios-instance

export interface TimetableData {
  id?: string
  name: string
  data: any
  constraints: string
  createdAt?: Date
  updatedAt?: Date
}

export interface GenerateTimetableParams {
  name: string
  periodCount: number
  schoolDays: string[]
  subjects: Array<{
    id: string
    name: string
  }>
  teachers: Array<{
    id: string
    name: string
    subjects: string[]
    availability?: any
  }>
  classes: Array<{
    id: string
    name: string
  }>
  lessons: Array<{
    classId: string
    subjectId: string
    teacherId: string
    periodsPerWeek: number
  }>
  constraints: {
    avoidBackToBack: boolean
    optimizeFreeTime: boolean
    balanceTeacherLoads: boolean
    maxDailyPeriodsPerTeacher?: number
    preferConsecutivePeriods?: boolean
  }
}

export interface TimetableResult {
  schedule?: Record<
    string,
    Array<{
      day: string
      period: number
      subject: string
      teacher: string
      room: string
    }>
  >
  scheduleData?: Array<{
    class: string
    [day: string]: any
  }>
  stats?: any
  conflicts?: Array<{
    type: string
    description: string
    entities: string[]
  }>
}

export interface ColorScheduleResult {
  class: string
  subject: string
  required_hours: number
  scheduled_hours: number
  uncompleted_hours: number
  teacherID: number
  teacherName: string
  divide: number[]
  alternateWith: null
  room: null
  available_slots: Array<{
    day: string
    time: string
    available_rooms: string[]
  }>
}

export interface HardConstraintResult {
  errors: string[]
}

export interface SoftConstraintResult {
  errors: {
    teacher_workload?: string[]
    teacher_consecutive_hours?: string[]
    [key: string]: string[] | undefined
  }
  error_counts: {
    [key: string]: number
  }
}

export interface TimeSlot {
  period: number
  start: string
  end: string
  isBreak: boolean
}

// Interface for the external timetable API
export interface ExternalTimetableParams {
  timeSlots: {
    [day: string]: {
      studyTimes: string[]
      restTime: string
    }
  }
  salles: Array<{ name: string; type: string }>
  classes: Array<{
    className: string
    subjects: Array<{
      subject: string
      hours: number
      teacherID: string | number
      divide?: number[]
      alternateWith?: string
      alternateWithTeacherID?: string | number
      alternationPattern?: string
      room?: string
    }>
  }>
  teachers: Array<{
    teacherId: string | number
    teacherName: string
    subjects: string[]
    unavailableTimes?: {
      [day: string]: string[]
    }
    minimumHours?: number
    maximumHours?: number
  }>
}

export type TimetableJobStatus =
  | 'queued'
  | 'validating'
  | 'building_model'
  | 'solving'
  | 'completed'
  | 'infeasible'
  | 'timed_out'
  | 'failed'
  | 'cancelled'

export interface TimetableJobResponse {
  job_id: string
  status: TimetableJobStatus
  result?: TimetableResult
  unresolved?: any[]
  error?: { code: string; message: string }
}

export const timetableService = {
  // Generate a new timetable using the internal API
  generateTimetable: async (
    params: GenerateTimetableParams
  ): Promise<TimetableResult> => {
    try {
      const response = await api.post('/api/api/timetable/generate', params)
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Get color-coded schedule for unscheduled lessons
  getColorSchedule: async (params: {
    data: ExternalTimetableParams
    result: TimetableResult
  }): Promise<ColorScheduleResult[]> => {
    try {
      const response = await axios.post(EXTERNAL_COLOR_SCHEDULE_API, params, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer 1d03360f4bcd533a1481905ed6bb9ae77bd650b901ee9c28bcf4dbffcc568575`,
        },
      })
      return response.data
    } catch (error) {
      // console.error('Error getting color schedule:', error)
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Validate hard constraints
  validateHardConstraints: async (params: {
    data: ExternalTimetableParams
    result: TimetableResult
  }): Promise<HardConstraintResult> => {
    try {
      const response = await axios.post(EXTERNAL_VALIDATE_HARD_API, params, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer 1d03360f4bcd533a1481905ed6bb9ae77bd650b901ee9c28bcf4dbffcc568575`,
        },
      })
      return response.data
    } catch (error) {
      // console.error('Error validating hard constraints:', error)
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Analyze soft constraints
  analyzeSoftConstraints: async (params: {
    data: ExternalTimetableParams
    result: TimetableResult
  }): Promise<SoftConstraintResult> => {
    try {
      const response = await axios.post(EXTERNAL_ANALYZE_SOFT_API, params, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer 1d03360f4bcd533a1481905ed6bb9ae77bd650b901ee9c28bcf4dbffcc568575`,
        },
      })
      return response.data
    } catch (error) {
      // console.error('Error analyzing soft constraints:', error)
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Generate a timetable using the external API job queue.
  generateExternalTimetable: async (
    params: ExternalTimetableParams,
    options?: { onJobAccepted?: (jobId: string) => void; signal?: AbortSignal }
  ): Promise<any> => {
    try {
      // Create a deep copy of the params to avoid mutating the original
      const processedParams = JSON.parse(JSON.stringify(params))
      
      // Process each day's time slots
      Object.keys(processedParams.timeSlots).forEach((day: string) => {
        const daySlots = processedParams.timeSlots[day]
        
        // Filter out empty, invalid, or disabled time slots
        daySlots.studyTimes = (daySlots.studyTimes || [])
          .filter((time: string) => {
            // Check if the time string has valid start and end times
            const [start, end] = time.split('=>').map((t: string) => t.trim())

            // Skip if start or end is empty
            if (!start || !end || start === ' ' || end === ' ') {
              return false
            }

            // Skip if start and end are the same (e.g., "8 => 8")
            if (start === end) {
              // console.log(
              //   `Filtering out invalid time slot: ${time} (start and end times are the same)`
              // )
              return false
            }

            return true
          })
          .map((time: string) => time.trim())

        // Format rest time if it's not empty
        if (
          daySlots.restTime &&
          typeof daySlots.restTime === 'string' &&
          daySlots.restTime.includes('=>')
        ) {
          const [start, end] = daySlots.restTime
            .split('=>')
            .map((t: string) => t.trim())

          // Only format if both start and end are valid
          if (start && end && start !== ' ' && end !== ' ') {
            // Skip if start and end are the same
            if (start === end) {
              // console.log(
              //   `Filtering out invalid rest time: ${daySlots.restTime} (start and end times are the same)`
              // )
              daySlots.restTime = ''
            } else if (start.includes(':') || end.includes(':')) {
              // Format time with colons
              daySlots.restTime = `${start} => ${end}`
            }
          } else {
            // Clear invalid rest time
            daySlots.restTime = ''
          }
        }
      })

      // // Final check to remove any days with empty study times
      // Object.keys(params.timeSlots).forEach((day) => {
      //   if (params.timeSlots[day].studyTimes.length === 0) {
      //  console.log(
      //       `Warning: Day ${day} has no valid study times. Adding a default time slot.`
      //     )
      //     // Add a default time slot to avoid API errors
      //     params.timeSlots[day].studyTimes = ['8 => 9']
      //   }
      // })

      try {
        const response = await axios.post(EXTERNAL_TIMETABLE_API, processedParams, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer 1d03360f4bcd533a1481905ed6bb9ae77bd650b901ee9c28bcf4dbffcc568575`,
          },
          signal: options?.signal,
        })
        const jobId = response.data?.job_id
        if (!jobId) {
          throw new Error('Timetable service did not return a job ID.')
        }
        options?.onJobAccepted?.(jobId)

        const pollDeadline = Date.now() + 30 * 60 * 1000
        let job: TimetableJobResponse
        while (true) {
          if (Date.now() >= pollDeadline) {
            throw new Error(
              `Timetable job ${jobId} is still running. You can retry polling it later.`
            )
          }
          await new Promise((resolve, reject) => {
            const handleAbort = () => {
              window.clearTimeout(timer)
              reject(new DOMException('Request cancelled', 'AbortError'))
            }
            const timer = window.setTimeout(() => {
              options?.signal?.removeEventListener('abort', handleAbort)
              resolve(undefined)
            }, 2000)
            options?.signal?.addEventListener('abort', handleAbort, { once: true })
          })
          const pollResponse = await axios.get<TimetableJobResponse>(
            `${EXTERNAL_TIMETABLE_JOBS_API}/${jobId}`,
            {
              headers: {
                Accept: 'application/json',
                Authorization: `Bearer 1d03360f4bcd533a1481905ed6bb9ae77bd650b901ee9c28bcf4dbffcc568575`,
              },
              signal: options?.signal,
            }
          )
          job = pollResponse.data
          if (job.status === 'completed') break
          if (['infeasible', 'timed_out', 'failed', 'cancelled'].includes(job.status)) {
            throw new Error(job.error?.message || `Timetable job ${job.status}.`)
          }
        }

        const completedData = {
          ...(job.result || {}),
          unresolved: job.unresolved || [],
          job_id: jobId,
        }
        return completedData
      } catch (requestError) {
        if (axios.isCancel(requestError)) {
          throw new Error('Timetable generation request was cancelled.')
        }
        throw requestError
      }
    } catch (error) {
      // console.error('API ERROR:', error)
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  cancelExternalTimetable: async (jobId: string): Promise<TimetableJobResponse> => {
    const response = await axios.delete<TimetableJobResponse>(
      `${EXTERNAL_TIMETABLE_JOBS_API}/${jobId}`,
      {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer 1d03360f4bcd533a1481905ed6bb9ae77bd650b901ee9c28bcf4dbffcc568575`,
        },
      }
    )
    return response.data
  },

  // Save generated timetable to the internal API
  saveTimetable: async (timetableData: any): Promise<any> => {
    try {
      // Process the data to ensure it's in the correct format for port 3000
      let processedData = timetableData.data

      // If the data has scheduleData, process it to ensure time formats are consistent
      if (
        processedData &&
        processedData.scheduleData &&
        Array.isArray(processedData.scheduleData)
      ) {
    // console.logProcessing scheduleData before saving to port 3000')

        // Define the correct day order
        const dayOrder = [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ]

        // Process each class schedule
        processedData.scheduleData = processedData.scheduleData.map(
          (classSchedule: any) => {
            const result = { ...classSchedule }

            // Process each day's schedule
            dayOrder.forEach((day) => {
              if (classSchedule[day] && Array.isArray(classSchedule[day])) {
                result[day] = classSchedule[day].map((period: any) => {
                  // Transform the period data to match the database schema
                  return {
                    id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                    day: day,
                    subject: period.subject,
                    timeSlot: period.time,
                    time: period.time,
                    className: classSchedule.class,
                    teacherId: period.teacherID,
                    teacherID: period.teacherID,
                    teacher: period.teacher,
                    classroomId: period.salle,
                    salle: period.salle,
                    group1: period.group1 || null,
                    group2: period.group2 || null,
                  }
                })
              } else {
                result[day] = []
              }
            })

            return result
          }
        )

        // Flatten the schedule data into entries
        const entries = processedData.scheduleData.flatMap(
          (classSchedule: any) => {
            return dayOrder.flatMap((day) => {
              return classSchedule[day].map((entry: any) => ({
                id: entry.id,
                day: entry.day,
                subject: entry.subject,
                timeSlot: entry.timeSlot,
                time: entry.time || entry.timeSlot,
                className: entry.className,
                teacherId: entry.teacherId,
                teacherID: entry.teacherID || entry.teacherId,
                teacher: entry.teacher,
                classroomId: entry.classroomId,
                salle: entry.salle || entry.classroomId,
                group1: entry.group1,
                group2: entry.group2,
              }))
            })
          }
        )

        // Update the processed data with the flattened entries
        processedData = {
          ...processedData,
          entries: entries,
        }
      }

      // Prepare the data structure for saving
      // We need to format it according to what the analyze feature expects
      const formattedData = {
        data: {
          ...(timetableData.inputData || timetableData.originalInputData || {}),
          // Ensure timeSlots are included in the data object
          timeSlots:
            timetableData.data?.timeSlots || processedData.timeSlots || {},
        },
        result: {
          scheduleData: processedData.scheduleData,
          // Also include timeSlots in the result for backward compatibility
          timeSlots:
            timetableData.data?.timeSlots || processedData.timeSlots || {},
        },
      }

      // Store the original input data separately to ensure it's saved properly
      // const originalInputData = timetableData.inputData || timetableData.originalInputData

      // console.log(
      //   'Original input data for saving:',
      //   originalInputData ? 'PRESENT' : 'MISSING'
      // )
  // console.logFormatted data for saving:', formattedData)

      // Check if there's an existing timetable for the establishment
      try {
        const checkResponse = await api.get('/timetable/etablissement')

        if (
          checkResponse.status === 200 &&
          checkResponse.data &&
          checkResponse.data.id
        ) {
          // We found an existing timetable, use PATCH instead
          // console.log(
          //   'Found existing timetable, using PATCH instead with ID:',
          //   checkResponse.data.id
          // )

          // For PATCH, update both data and result fields, and include inputData
          const patchData = {
            data: formattedData,
            description: timetableData.description || 'Generated Timetable',
            academicYear:
              timetableData.academicYear || new Date().getFullYear().toString(),
            isActive: true,
            // Explicitly include the inputData field at the top level for PATCH requests too
            inputData:
              timetableData.inputData || timetableData.originalInputData,
          }

          // console.log(
          //   'Updating timetable with inputData:',
          //   patchData.inputData ? 'PRESENT' : 'MISSING'
          // )

          const response = await api.patch(
            `/timetable/${checkResponse.data.id}`,
            patchData
          )

          return response.data
        }
      } catch (checkError) {
        // console.error('Error checking for existing timetable:', checkError)
        // Continue with POST if check fails
      }

      // If no existing timetable or check failed, use POST
      // Make sure we include all the necessary data including inputData
      const requestData = {
        data: formattedData,
        description: timetableData.description || 'Generated Timetable',
        academicYear:
          timetableData.academicYear || new Date().getFullYear().toString(),
        isActive: true,
        // Explicitly include the inputData field at the top level
        inputData: timetableData.inputData || timetableData.originalInputData,
      }

      // console.log(
      //   'Saving timetable with inputData:',
      //   requestData.inputData ? 'PRESENT' : 'MISSING'
      // )

      const response = await api.post('/timetable', requestData)

      return response.data
    } catch (error) {
      // console.error('Error saving timetable:', error)
      throw error
    }
  },

  // Check timetable generation limit before saving
  checkTimetableLimit: async (): Promise<{
    canCreate: boolean
    isActive: boolean
    timetableGenerationCount: number
    examGenerationCount?: number
    remaining: number
    message: string | null
  }> => {
    try {
  // console.log🔴 [checkTimetableLimit] Starting API call...')
  // console.log🔴 [checkTimetableLimit] Endpoint: GET /timetable/check-limit')
  // console.log🔴 [checkTimetableLimit] Full URL will be:', `${api.defaults.baseURL || ''}/timetable/check-limit`)
  // console.log🔴 [checkTimetableLimit] Making request now...')
      
      const response = await api.get('/timetable/check-limit')
      
  // console.log🔴 [checkTimetableLimit] Response received!')
  // console.log🔴 [checkTimetableLimit] Response status:', response.status)
  // console.log🔴 [checkTimetableLimit] Response headers:', response.headers)
  // console.log🔴 [checkTimetableLimit] Response data:', response.data)
  // console.log🔴 [checkTimetableLimit] Response data type:', typeof response.data)
      
      return response.data
    } catch (error) {
      // console.error('🔴 [checkTimetableLimit] ERROR caught!')
      // console.error('🔴 [checkTimetableLimit] Error type:', typeof error)
      // console.error('🔴 [checkTimetableLimit] Error:', error)
      
      const axiosError = error as AxiosError
      // console.error('🔴 [checkTimetableLimit] Axios error response status:', axiosError.response?.status)
      // console.error('🔴 [checkTimetableLimit] Axios error response data:', axiosError.response?.data)
      
      // If 400 error, return the error data
      if (axiosError.response?.status === 400) {
    // console.log🔴 [checkTimetableLimit] Returning 400 error as limit reached')
        const errorData = axiosError.response.data as any
        return {
          canCreate: false,
          isActive: false,
          timetableGenerationCount: 0,
          remaining: 0,
          message: errorData?.message || 'You have reached your free trial limit.'
        }
      }
      // On other errors, allow save (backend will check again)
      // console.error('🔴 [checkTimetableLimit] Re-throwing error')
      throw error
    }
  },

  // Increment timetable generation count
  incrementTimetableCount: async (): Promise<void> => {
    try {
  // console.log🔴 [incrementTimetableCount] Calling POST /timetable/increment-count')
      await api.post('/timetable/increment-count')
  // console.log🔴 [incrementTimetableCount] Count incremented successfully')
    } catch (error) {
      // console.error('🔴 [incrementTimetableCount] Error incrementing count:', error)
      // Don't throw - this is not critical, just logging
    }
  },

  // Get all timetables
  getTimetables: async (): Promise<TimetableData[]> => {
    try {
      const response = await api.get('/api/api/timetable')
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Get a timetable by ID
  getTimetableById: async (id: string): Promise<TimetableData> => {
    try {
      const response = await api.get(`/api/api/timetable/${id}`)
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Update a timetable
  updateTimetable: async (
    id: string,
    data: Partial<TimetableData>
  ): Promise<TimetableData> => {
    try {
      const response = await api.patch(`/api/api/timetable/${id}`, data)
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Delete a timetable
  deleteTimetable: async (id: string): Promise<void> => {
    try {
      await api.delete(`/api/api/timetable/${id}`)
    } catch (error) {
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Manual adjustments to timetable
  adjustTimetable: async (
    id: string,
    adjustments: Array<{
      classId: string
      day: string
      period: number
      subjectId?: string
      teacherId?: string
      roomId?: string
    }>
  ): Promise<TimetableResult> => {
    try {
      const response = await api.post(`/api/api/timetable/${id}/adjust`, {
        adjustments,
      })
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Export timetable to PDF
  exportToPdf: async (
    id: string,
    viewType: 'class' | 'teacher' | 'room'
  ): Promise<Blob> => {
    try {
      const response = await api.get(
        `/api/api/timetable/${id}/export/pdf?viewType=${viewType}`,
        {
          responseType: 'blob',
        }
      )
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Export timetable to Excel
  exportToExcel: async (
    id: string,
    viewType: 'class' | 'teacher' | 'room'
  ): Promise<Blob> => {
    try {
      const response = await api.get(
        `/api/api/timetable/${id}/export/excel?viewType=${viewType}`,
        {
          responseType: 'blob',
        }
      )
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Get timetable for the current teacher
  getTeacherTimetable: async (): Promise<any> => {
    try {
  // console.logFetching timetable for current teacher')

      // Make the request - HTTP-only cookies are sent automatically via api instance
      const response = await api.get('/timetable/teacher', {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })

  // console.logTeacher timetable fetched successfully:', response.data)
      return response.data
    } catch (error) {
      // console.error('Error fetching teacher timetable:', error)
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Get timetable for a specific student class
  getStudentTimetable: async (className?: string): Promise<any> => {
    try {
      // console.log(
      //   'Fetching timetable for student class:',
      //   className || 'current student'
      // )

      let url = '/timetable/student'
      if (className) {
        url += `?className=${encodeURIComponent(className)}`
      }

      // Make the request - HTTP-only cookies are sent automatically via api instance
      const response = await api.get(url, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })

  // console.logStudent timetable fetched successfully:', response.data)
      return response.data
    } catch (error) {
      // console.error('Error fetching student timetable:', error)
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Get students for a specific class
  getStudentsForClass: async (className: string): Promise<any> => {
    try {
  // console.logFetching students for class:', className)
      const response = await api.get(
        `/timetable/class-students/${encodeURIComponent(className)}`
      )
  // console.logStudents fetched successfully for class:', className)
      return response.data
    } catch (error) {
      // console.error('Error fetching students for class:', error)
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },

  // Get time slots from the external API
  getTimeSlots: async (): Promise<{ timeSlots: TimeSlot[] }> => {
    try {
  // console.logFetching time slots from external API (GET method)')
      const response = await axios.get(EXTERNAL_TIME_SLOTS_API, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer 1d03360f4bcd533a1481905ed6bb9ae77bd650b901ee9c28bcf4dbffcc568575`,
        },
      })
  // console.logTime slots fetched successfully:', response.data)

      // Format 1: Array of time slots
      if (
        response.data &&
        response.data.timeslots &&
        Array.isArray(response.data.timeslots)
      ) {
    // console.logProcessing time slots in array format')
        return {
          timeSlots: response.data.timeslots.map((slot: any) => ({
            period: slot.period,
            start: slot.start,
            end: slot.end,
            isBreak: slot.isBreak || false,
          })),
        }
      }

      // Direct format: timeSlots array
      if (
        response.data &&
        response.data.timeSlots &&
        Array.isArray(response.data.timeSlots)
      ) {
    // console.logProcessing direct timeSlots array format')
        return {
          timeSlots: response.data.timeSlots,
        }
      }

      // Format 2: Nested structure by days
      if (
        response.data &&
        response.data.timeSlots &&
        typeof response.data.timeSlots === 'object'
      ) {
    // console.logProcessing time slots in nested day format')
        const timeSlots: TimeSlot[] = []
        let periodCounter = 1

        // Process each day's time slots
        Object.entries(response.data.timeSlots).forEach(
          ([_day, dayData]: [string, any]) => {
            // Process study times
            if (dayData.studyTimes && Array.isArray(dayData.studyTimes)) {
              dayData.studyTimes.forEach((timeStr: string) => {
                const [start, end] = timeStr.split(' => ')

                // Check if this time slot already exists
                const existingSlot = timeSlots.find(
                  (slot) =>
                    slot.start === start && slot.end === end && !slot.isBreak
                )

                if (!existingSlot) {
                  timeSlots.push({
                    period: periodCounter++,
                    start,
                    end,
                    isBreak: false,
                  })
                }
              })
            }

            // Process rest time (break)
            if (dayData.restTime) {
              const [start, end] = dayData.restTime.split(' => ')

              // Check if this break already exists
              const existingBreak = timeSlots.find(
                (slot) =>
                  slot.start === start && slot.end === end && slot.isBreak
              )

              if (!existingBreak) {
                timeSlots.push({
                  period: periodCounter++,
                  start,
                  end,
                  isBreak: true,
                })
              }
            }
          }
        )

        // Sort time slots by start time
        timeSlots.sort((a, b) => {
          const getMinutes = (time: string) => {
            const [hours, minutes] = time.split(':').map(Number)
            return hours * 60 + minutes
          }
          return getMinutes(a.start) - getMinutes(b.start)
        })

        // Reassign period numbers after sorting
        timeSlots.forEach((slot, index) => {
          slot.period = index + 1
        })

        return { timeSlots }
      }

      // Format 3: Already in the expected format
      if (
        response.data &&
        response.data.timeSlots &&
        Array.isArray(response.data.timeSlots)
      ) {
    // console.logTime slots already in expected format')
        return response.data
      }

      // If we can't process the data, return an empty array
      console.warn('Could not process time slots data, returning empty array')
      return { timeSlots: [] }
    } catch (error) {
      // console.error('Error fetching time slots:', error)
      const axiosError = error as AxiosError
      throw axiosError.response?.data || error
    }
  },
}
